import { ScheduleModule } from '@nestjs/schedule';
import { Module } from '@nestjs/common';
import { APP_GUARD } from '@nestjs/core';
import { ThrottlerGuard, ThrottlerModule } from '@nestjs/throttler';
import { AppController } from './app.controller';
import { AppService } from './app.service';
import { AddressesModule } from './addresses/addresses.module';
import { AnalyticsModule } from './analytics/analytics.module';
import { AuditModule } from './audit/audit.module';
import { AuthModule } from './auth/auth.module';
import { CartModule } from './cart/cart.module';
import { CategoriesModule } from './categories/categories.module';
import { CheckoutModule } from './checkout/checkout.module';
import { CommerceAdminModule } from './commerce-admin/commerce-admin.module';
import { DatabaseModule } from './database/database.module';
import { HealthModule } from './health/health.module';
import { DemoCleanupModule } from './demo-cleanup/demo-cleanup.module';
import { OrdersModule } from './orders/orders.module';
import { MerchantModule } from './merchant/merchant.module';
import { PlatformModule } from './platform/platform.module';
import { ProductsModule } from './products/products.module';
import { ReviewsModule } from './reviews/reviews.module';
import { TenancyModule } from './tenancy/tenancy.module';
import { UsersModule } from './users/users.module';
import { WishlistModule } from './wishlist/wishlist.module';
import { ThemesModule } from './themes/themes.module';
// Storefront layout templates — distinct from TemplatesModule (email) below.
import { StorefrontTemplatesModule } from './storefront-templates/storefront-templates.module';
import { BrandingModule } from './branding/branding.module';
import { ShippingModule } from './shipping/shipping.module';
import { ReadinessModule } from './readiness/readiness.module';
import { InventoryModule } from './inventory/inventory.module';
import { InvoicesModule } from './invoices/invoices.module';
import { ImagesModule } from './images/images.module';
import { MediaModule } from './media/media.module';
import { PaymentMethodsModule } from './payment-methods/payment-methods.module';
import { RazorpayModule } from './razorpay/razorpay.module';
import { TemplatesModule } from './templates/templates.module';

@Module({
  imports: [
    // 1000 req/min per IP is the platform-wide default (see docs/08-deployment.md
    // "Rate limiting" for the full rationale) — raised from 10 after real
    // admin usage (store-switcher reloads, dashboard's several parallel
    // fetches) tripped the old limit under normal, non-abusive use.
    ThrottlerModule.forRoot([{ ttl: 60000, limit: 1000 }]),
    TenancyModule,
    AuditModule,
    AnalyticsModule,
    AddressesModule,
    AuthModule,
    CartModule,
    CategoriesModule,
    CheckoutModule,
    CommerceAdminModule,
    // Registered ONCE, here, and nowhere else.
    //
    // forRoot() is `global: true`, so one call serves the whole app — but each
    // additional call adds another ScheduleExplorer, and every explorer walks
    // every provider and registers every @Cron it finds. Three feature modules
    // each called it, so every scheduled job in this API ran THREE TIMES per
    // tick. Measured, not inferred: a 20ms @Interval fired 5 times in 130ms
    // with one registration and 12 with three.
    //
    // What that cost in production: three concurrent pg_basebackup runs sharing
    // one staging directory and deleting each other's files, and concurrent
    // checkSla() sweeps racing to insert the same alert row, which is where the
    // hourly P2002 duplicate-key error came from.
    ScheduleModule.forRoot(),
    DatabaseModule,
    HealthModule,
    DemoCleanupModule,
    OrdersModule,
    MerchantModule,
    PlatformModule,
    ProductsModule,
    ReviewsModule,
    UsersModule,
    WishlistModule,
    ThemesModule,
    StorefrontTemplatesModule,
    BrandingModule,
    ShippingModule,
    ReadinessModule,
    ImagesModule,
    InventoryModule,
    InvoicesModule,
    MediaModule,
    PaymentMethodsModule,
    RazorpayModule,
    TemplatesModule,
  ],
  controllers: [AppController],
  providers: [
    AppService,
    { provide: APP_GUARD, useClass: ThrottlerGuard },
  ],
})
export class AppModule {}
